import { useState } from 'react'

import { ActionIcon, Badge, Box, Menu, NumberInput } from '@mantine/core'
import dayjs from 'dayjs'

import DNButton from 'src/components/Buttons/Button/DNButton'
import DateFilter, {
  DatePreset,
  DateRangeValue,
} from 'src/components/Inputs/DateFilter/DateFilter'
import IconCheckmark from 'src/icons/IconCheckmark'
import IconChevronLeft from 'src/icons/IconChevronLeft'
import IconFilter from 'src/icons/IconFilter'
import { formatSnakeValue } from 'src/lib/formatters'

export interface FiltersMenuOption {
  label: string
  value: string
}

interface CheckboxFiltersMenuFilter {
  type?: 'checkbox'
  title: string
  selected: string[]
  onChange: (selected: string[]) => void
  options: FiltersMenuOption[]
  alphabetize?: boolean
  formatValue?: (value: string) => string
}

interface DateRangeFiltersMenuFilter {
  type: 'daterange'
  title: string
  value?: DateRangeValue
  onChange: (value: DateRangeValue) => void
  maxDate?: Date
  allowFutureDates?: boolean
  presets?: DatePreset[]
}

interface RangeFiltersMenuFilter {
  type: 'range'
  title: string
  minValue?: number | null
  maxValue?: number | null
  min?: number
  max?: number
  onChange: (min: number | null, max: number | null) => void
}

export type FiltersMenuFilter =
  | CheckboxFiltersMenuFilter
  | DateRangeFiltersMenuFilter
  | RangeFiltersMenuFilter

const FiltersMenu = ({ filters }: { filters: FiltersMenuFilter[] }) => {
  const [selectedSubmenu, setSelectedSubmenu] = useState('')
  const [localMin, setLocalMin] = useState<number | string>('')
  const [localMax, setLocalMax] = useState<number | string>('')

  const openSubmenu = (title: string) => {
    const filter = filters.find((f) => f.title.toLowerCase() === title)
    if (filter?.type === 'range') {
      setLocalMin(filter.minValue ?? '')
      setLocalMax(filter.maxValue ?? '')
    }
    setSelectedSubmenu(title)
  }

  const getTotalSelectedCount = () =>
    filters.reduce((total, f) => {
      if (f.type === 'daterange')
        return total + (f.value?.gte || f.value?.lte ? 1 : 0)
      if (f.type === 'range')
        return total + (f.minValue != null || f.maxValue != null ? 1 : 0)
      return total + (f.selected?.length || 0)
    }, 0)

  const getMainListDisplayValue = (filter: FiltersMenuFilter) => {
    if (filter.type === 'daterange') {
      if (!filter.value?.gte && !filter.value?.lte) return null
      if (filter.value?.gte && filter.value?.lte)
        return `${dayjs(filter.value.gte).format('MM/DD')} – ${dayjs(filter.value.lte).format('MM/DD')}`
      if (filter.value?.gte)
        return `≥ ${dayjs(filter.value.gte).format('MM/DD')}`
      return `≤ ${dayjs(filter.value.lte).format('MM/DD')}`
    }
    if (filter.type === 'range') {
      const { minValue, maxValue } = filter
      if (minValue == null && maxValue == null) return null
      if (minValue != null && maxValue != null) return `${minValue} – ${maxValue}`
      if (minValue != null) return `≥ ${minValue}`
      return `≤ ${maxValue}`
    }
    if (!filter.selected?.length) return null
    const value = filter.selected[0]
    const label = filter.formatValue
      ? filter.formatValue(value)
      : filter.options?.find((opt) => opt.value === value)?.label ??
        formatSnakeValue(value)
    return filter.selected.length > 1
      ? `${label}, ${filter.selected.length - 1}+`
      : label
  }

  const handleClear = (filter: FiltersMenuFilter) => {
    if (filter.type === 'daterange') filter.onChange({ gte: null, lte: null })
    else if (filter.type === 'range') {
      setLocalMin('')
      setLocalMax('')
      filter.onChange(null, null)
    } else filter.onChange([])
  }

  const handleClearAll = () => {
    filters.forEach((f) => {
      if (f.type === 'daterange') f.onChange({ gte: null, lte: null })
      else if (f.type === 'range') f.onChange(null, null)
      else f.onChange([])
    })
    setLocalMin('')
    setLocalMax('')
  }

  const menuContents = () => {
    if (selectedSubmenu) {
      const activeFilter = filters.find(
        (f) => f.title.toLowerCase() === selectedSubmenu
      )
      if (!activeFilter) return null

      if (activeFilter.type === 'daterange') {
        return (
          <DateFilter
            onDateChange={(value) => activeFilter.onChange(value)}
            onClose={() => setSelectedSubmenu('')}
            maxDate={activeFilter.maxDate}
            allowFutureDates={activeFilter.allowFutureDates}
            presets={activeFilter.presets}
          />
        )
      }

      if (activeFilter.type === 'range') {
        const minNum = typeof localMin === 'string' ? null : localMin
        const maxNum = typeof localMax === 'string' ? null : localMax
        const isInvalid =
          minNum !== null && maxNum !== null && minNum > maxNum
        return (
          <div className="p-4">
            <div className="flex flex-row items-center gap-2">
              <NumberInput
                placeholder="Min"
                value={localMin}
                onChange={(value) => setLocalMin(value)}
                min={activeFilter.min}
                max={activeFilter.max}
                size="xs"
                className="flex-1"
              />
              <div className="text-xs text-doubleNickel-gray-500">to</div>
              <NumberInput
                placeholder="Max"
                value={localMax}
                onChange={(value) => setLocalMax(value)}
                min={activeFilter.min}
                max={activeFilter.max}
                size="xs"
                className="flex-1"
              />
            </div>
            {isInvalid && (
              <div className="mt-1 text-xs text-doubleNickel-error-600">
                Min cannot be greater than max
              </div>
            )}
            <div className="mt-3 flex flex-row justify-end">
              <button
                disabled={isInvalid}
                className="rounded bg-doubleNickel-brand-500 px-3 py-1 text-xs text-white hover:bg-doubleNickel-brand-600 disabled:cursor-not-allowed disabled:opacity-40"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  const newMin = typeof localMin === 'string' ? null : localMin
                  const newMax = typeof localMax === 'string' ? null : localMax
                  activeFilter.onChange(newMin, newMax)
                  setSelectedSubmenu('')
                }}
              >
                Apply
              </button>
            </div>
          </div>
        )
      }

      let optionsToDisplay = [...activeFilter.options]
      if (activeFilter.alphabetize !== false) {
        optionsToDisplay = optionsToDisplay.sort((a, b) =>
          a.label.localeCompare(b.label)
        )
      }
      return (
        <>
          {optionsToDisplay.map((option) => (
            <Box
              key={option.value}
              className="flex flex-row items-center justify-between rounded p-2 px-2 text-xs hover:bg-doubleNickel-gray-100"
              onClick={() => {
                const newSelected = activeFilter.selected.includes(option.value)
                  ? activeFilter.selected.filter((v) => v !== option.value)
                  : [...activeFilter.selected, option.value]
                activeFilter.onChange(newSelected)
              }}
            >
              {option.label}
              {activeFilter.selected.includes(option.value) && (
                <IconCheckmark className="h-4 w-4 fill-none stroke-doubleNickel-gray-500" />
              )}
            </Box>
          ))}
        </>
      )
    }

    return (
      <>
        {filters
          .slice()
          .sort((a, b) => a.title.localeCompare(b.title))
          .map((filter) => (
            <Menu.Item
              key={filter.title}
              rightSection={
                <IconChevronLeft className="rotate-180 fill-none stroke-doubleNickel-gray-400" />
              }
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                openSubmenu(filter.title.toLowerCase())
              }}
            >
              <Box className="flex flex-row items-center justify-between py-1 text-xs font-medium text-doubleNickel-gray-800">
                {filter.title}
                <div className="font-normal text-doubleNickel-gray-500">
                  {getMainListDisplayValue(filter)}
                </div>
              </Box>
            </Menu.Item>
          ))}
      </>
    )
  }

  return (
    <Menu
      shadow="md"
      width={275}
      position="bottom-end"
      withArrow
      arrowPosition="center"
      closeOnItemClick={false}
      onClose={() => setSelectedSubmenu('')}
    >
      <Menu.Target>
        <DNButton variant="outline">
          <IconFilter className="h-4 fill-none stroke-doubleNickel-gray-700" />
          Filters
          <Badge size="xs" color="gray" variant="light">
            {getTotalSelectedCount()}
          </Badge>
        </DNButton>
      </Menu.Target>

      <Menu.Dropdown className="rounded-lg">
        <div className="flex flex-col gap-2 py-2">
          <div className="flex flex-row items-center pr-4 text-sm">
            {selectedSubmenu && (
              <ActionIcon
                variant="subtle"
                onClick={() => setSelectedSubmenu('')}
              >
                <IconChevronLeft className="fill-none stroke-doubleNickel-gray-500" />
              </ActionIcon>
            )}
            <Menu.Label className="capitalize">
              {selectedSubmenu || 'Filters'}
            </Menu.Label>
            <Box
              className="ml-auto text-xs text-doubleNickel-brand-500 hover:cursor-pointer hover:underline"
              onClick={() => {
                if (selectedSubmenu) {
                  const filter = filters.find(
                    (f) => f.title.toLowerCase() === selectedSubmenu
                  )
                  if (filter) handleClear(filter)
                } else {
                  handleClearAll()
                }
              }}
            >
              {selectedSubmenu ? 'Clear' : 'Clear all'}
            </Box>
          </div>
          <div className="max-h-[450px] overflow-y-auto">{menuContents()}</div>
        </div>
      </Menu.Dropdown>
    </Menu>
  )
}

export default FiltersMenu
